import * as React from "react";
import styled, { css } from "styled-components";

import { Text } from "src/library/components/Text/Text";
import { CreateOrderContext } from "src/core/shared/components/HeaderContext";
import { useDrop } from "src/core/drops/hooks";
import { OrderOrigin } from "src/core/orders/api/orders.api";
import { useOrder } from "src/core/orders/hooks";
import { IProduct } from "src/core/products/api/products.api";
import useProductIsOrderable from "src/core/products/hooks/useProductIsOrderable";

import { ICallOff } from "../api/call-off.api";

interface CallOffRowProps {
 callOff: ICallOff
 display: "small"|"large"
}

const CustomerCallOffRow: React.ReactFCWithChildren<CallOffRowProps> = ({ callOff, display }) => {
  const [ { drop } ] = useDrop();
  const [ { isCreatingOrder }, { createOrder } ] = useOrder();
  const [ limitedInfo, setLimitedInfo ] = React.useState(false);
  const wrapperRef = React.createRef<HTMLDivElement>();
  const productOrderable = useProductIsOrderable(callOff.product, undefined, true);

  React.useEffect(() => {
    if (!wrapperRef.current) {
      return;
    }

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.contentRect.width;

        if (width < 750 && display !== "small") {
          setLimitedInfo(true);
        } else {
          setLimitedInfo(false);
        }
      }
    });

    observer.observe(wrapperRef.current);

    return () => {
      observer.disconnect();
    };
  }, [ display, limitedInfo ]);

  const canOrder = React.useMemo(() => productOrderable.orderable && drop, [ drop, productOrderable.orderable ]);

  const CreateOrderContextButton = React.useCallback((product: IProduct) => {
    if (productOrderable.orderable) {
      return (
        <>
          {drop && (
            <CreateOrderContext
              direction="overlay"
              drop={drop}
              $buttonType="submit"
              loading={isCreatingOrder}
              product={product}
              defaultVariantId={product.orderVariantId}
              createDraftOrder={(date, options) => {
                createOrder({
                  date,
                  origin: OrderOrigin.Manual,
                  products: [ product ],
                  orderVariantId: product?.orderVariantId,
                  dropOverride: options?.drop
                });
              }}
            />
          )}
        </>
      );
    }

    return (
      <Text color="error">
        {productOrderable.unavailableMessage}
      </Text>
    );
  }, [
    createOrder,
    drop,
    isCreatingOrder,
    productOrderable.orderable,
    productOrderable.unavailableMessage
  ]);

  return (
    <CustomerCallOffRowWrapper
      display={display}
      ref={wrapperRef}
    >

      <div className="info-row">
        <Text as="h4">
          Name
        </Text>

        <Text>
          {callOff.name}
        </Text>
      </div>

      {!limitedInfo && (
        <div className="info-row">
          <Text as="h4">
            Customer Reference
          </Text>

          <Text>
            {callOff.customerReference}
          </Text>
        </div>
      )}

      <div className="info-row">
        <Text as="h4">
          Product
        </Text>

        <Text>
          {`${callOff.product?.partNumber} (${callOff.product?.description})`}
        </Text>
      </div>

      <div className="info-row">
        <Text as="h4">
          Quantity Remaining
        </Text>

        <Text>
          {callOff.currentQuantity}
        </Text>
      </div>

      <div className="button-wrapper">
        {canOrder ? (
          <CreateOrderContext
            direction="overlay"
            drop={drop}
            $buttonType="submit"
            loading={isCreatingOrder}
            product={callOff.product}
            defaultVariantId={callOff.product.orderVariantId}
            createDraftOrder={(date, options) => {
              createOrder({
                date,
                origin: OrderOrigin.Manual,
                products: [ callOff.product ],
                orderVariantId: callOff.product?.orderVariantId,
                dropOverride: options?.drop
              });
            }}
          />
        ) : productOrderable && (
          <Text color="error">
            {productOrderable.unavailableMessage}
          </Text>
        )}
      </div>

    </CustomerCallOffRowWrapper>
  );
};

export default CustomerCallOffRow;

const CustomerCallOffRowWrapper = styled.div<{display: "small"|"large"}>`
  ${({ theme, display }) => css`
    width: 100%;
    padding: ${theme.spacing.padding.sm};
    border: 2px solid ${theme.colors.lightGrey};
    margin-bottom: ${theme.spacing.margins.sm};
    margin-right: ${theme.spacing.margins.sm};
    display: flex;
    flex-direction: ${display === "small" ? "column" : "row"};
    justify-content: space-between;
    align-items: center;
    width:${display === "small" ? "250px" : ""};


    .info-icon {
      margin-right: ${theme.spacing.margins.sm};
    }

    
    .info-row {
      flex:1;
      display: flex;
      flex-direction: ${display === "small" ? "row" : "column"};
      gap: ${display === "small" ? theme.spacing.padding.sm : "0px"};
      margin-right: ${theme.spacing.margins.sm};
    }
    
    .button-wrapper {
      text-align:center
    }
  `}
`;
